import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Bed, Bath, Square, ShoppingBag, Mountain, GraduationCap, TreePine, TrendingUp, Home, Utensils, Heart, Car, Plane, Zap } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$475,000',
  address: '6234 Palmyra Ave',
  area: 'Spring Valley',
  beds: 3,
  baths: 2,
  sqft: '1,850',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$625,000',
  address: '8901 W Flamingo Rd',
  area: 'The Lakes',
  beds: 4,
  baths: 3,
  sqft: '2,400',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$550,000',
  address: '3421 Desert Bloom Ct',
  area: 'Spring Valley',
  beds: 4,
  baths: 2.5,
  sqft: '2,200',
  image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
}];
export function SpringValleyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-1820">
      <SEOHead title="Spring Valley Real Estate & Homes for Sale | Jenny Gong" description="Find homes in Spring Valley, Las Vegas. Centrally located with diverse luxury options, expansive lots, and easy access to the Las Vegas Strip." keywords="Spring Valley real estate, Spring Valley homes for sale, The Lakes Las Vegas real estate, Spanish Trail homes for sale" canonicalUrl="https://jennygong.com/spring-valley" ogImage="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop" data-id="element-1821" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-1822">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop")'
      }} data-id="element-1823">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-1824"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-1825"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-1826">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-1827">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-1828">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-1829">
              Spring Valley Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-1830">
              Centrally located with diverse housing options and unbeatable
              access to everything Las Vegas has to offer.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-1831">
        <div className="max-w-7xl mx-auto" data-id="element-1832">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1833">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1834">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1835">
                The Heart of <span className="italic text-gold" data-id="element-1836">Las Vegas</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1837">
                <p data-id="element-1838">
                  Spring Valley is one of the most centrally located communities
                  in the Las Vegas Valley, making it a favorite for
                  professionals, families, and anyone who values convenience.
                  With a population of over 178,000, it's a vibrant and diverse
                  area that puts you minutes from the Strip,{' '}
                  <Link to="/summerlin" className="text-gold hover:underline" data-id="element-1839">
                    Summerlin
                  </Link>
                  , and{' '}
                  <Link to="/henderson" className="text-gold hover:underline" data-id="element-1840">
                    Henderson
                  </Link>
                  .
                </p>
                <p data-id="element-1841">
                  The community is known for its excellent value — offering
                  spacious homes with large lots at prices well below
                  neighboring{' '}
                  <Link to="/summerlin" className="text-gold hover:underline" data-id="element-1842">
                    Summerlin
                  </Link>
                  . You'll find everything from charming single-story ranch
                  homes in the $400K range to stunning custom estates exceeding
                  $1M in gated communities like The Lakes.
                </p>
                <p data-id="element-1843">
                  Spring Valley is also home to some of the best Asian cuisine
                  in Las Vegas, centered around the famous Chinatown district
                  along Spring Mountain Road — one of the most acclaimed food
                  corridors in the entire country.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-1844">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1845">
                <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1846" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1847">10m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1848">
                  To The Strip
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1849">
                <Utensils className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1850" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1851">#1</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1852">
                  Food Scene
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1853">
                <Zap className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1854" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1855">Best</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1856">
                  Value in Vegas
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1857">
                <Sun className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1858" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1859">
                  178K+
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1860">
                  Residents
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chinatown & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1861">
        <div className="max-w-7xl mx-auto" data-id="element-1862">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1863">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1864">
              <div className="relative h-[500px]" data-id="element-1865">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover" data-id="element-1866" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1867"></div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1868">
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1869">
                Culinary Capital
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1870">
                Chinatown & Dining Scene
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1871">
                <p data-id="element-1872">
                  Spring Mountain Road, running through the heart of Spring
                  Valley, is home to Las Vegas's legendary Chinatown — a 3+ mile
                  stretch of incredible Asian restaurants, bakeries, and markets
                  that has been called one of the best food streets in America.
                </p>
                <p data-id="element-1873">
                  From authentic ramen and dim sum to Korean BBQ and Vietnamese
                  pho, the dining options are world-class and incredibly
                  affordable. It's a major draw for foodies and a point of pride
                  for Spring Valley residents.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Areas */}
      <section className="py-24 px-6 bg-white" data-id="element-1874">
        <div className="max-w-7xl mx-auto" data-id="element-1875">
          <div className="text-center mb-16" data-id="element-1876">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1877">
              Neighborhoods
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1878">Popular Areas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1879">
            {[{
            name: 'The Lakes',
            price: '$500K - $1.2M',
            desc: 'A prestigious gated community centered around a beautiful man-made lake with waterfront homes and lush landscaping.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Desert Shores',
            price: '$350K - $700K',
            desc: 'A unique lakefront community with four man-made lakes, sandy beaches, and a resort-like atmosphere.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop'
          }, {
            name: 'Canyon Gate',
            price: '$400K - $900K',
            desc: 'Guard-gated golf course community with mature trees, a Ted Robinson-designed course, and elegant homes.',
            img: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=2787&auto=format&fit=crop'
          }].map((n, idx) => <motion.div key={n.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-warmGray p-6 border border-warmGray-dark" data-id="element-1880">
                <img src={n.img} alt={n.name} className="w-full h-48 object-cover mb-4" data-id="element-1881" />
                <div className="flex justify-between items-start mb-3" data-id="element-1882">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-1883">{n.name}</h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded whitespace-nowrap" data-id="element-1884">
                    {n.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1885">
                  {n.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Convenience */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-1886">
        <div className="max-w-7xl mx-auto" data-id="element-1887">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1888">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="space-y-8" data-id="element-1889">
              <div data-id="element-1890">
                <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1891">
                  Unbeatable Location
                </span>
                <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1892">
                  Everything Within Reach
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1893">
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1894">
                  <ShoppingBag className="w-6 h-6 text-gold mb-3" data-id="element-1895" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1896">
                    Shopping
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1897">
                    Minutes from Fashion Show Mall, Downtown Summerlin, and
                    countless local shops.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1898">
                  <Mountain className="w-6 h-6 text-gold mb-3" data-id="element-1899" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1900">
                    Red Rock Canyon
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1901">
                    Just 20 minutes to world-class hiking and the stunning
                    scenic loop drive.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1902">
                  <TreePine className="w-6 h-6 text-gold mb-3" data-id="element-1903" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1904">
                    Parks & Recreation
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1905">
                    Desert Breeze Park features a skate park, sports fields, and
                    a popular community center.
                  </p>
                </div>
                <div className="bg-white p-6 border border-warmGray-dark" data-id="element-1906">
                  <GraduationCap className="w-6 h-6 text-gold mb-3" data-id="element-1907" />
                  <h4 className="font-serif text-lg text-charcoal mb-2" data-id="element-1908">
                    Great Schools
                  </h4>
                  <p className="text-charcoal-light font-light text-sm" data-id="element-1909">
                    Home to Bonanza High School, Spring Valley High School, and
                    several top-rated elementary schools.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1910">
              <div className="relative h-[600px]" data-id="element-1911">
                <img src="https://images.unsplash.com/photo-1449844908441-8829872d2607?q=80&w=2940&auto=format&fit=crop" alt="Spring Valley Living" className="w-full h-full object-cover" data-id="element-1912" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1913"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 px-6 bg-white" data-id="element-1914">
        <div className="max-w-7xl mx-auto" data-id="element-1915">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1916">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1917" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1918">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1919">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-1920">
              Spring Valley is served by established Clark County School
              District institutions and offers excellent private educational
              alternatives.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1921">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1922">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1923">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1924">
                <li data-id="element-1925">• Lucille S. Rogers Elementary</li>
                <li data-id="element-1926">• Patricia A. Bendorf Elementary</li>
                <li data-id="element-1927">• Marion Earl Elementary</li>
                <li data-id="element-1928">• Grant Bowler Elementary</li>
                <li data-id="element-1929">• Victoria Fertitta Middle School</li>
                <li data-id="element-1930">• Clifford J. Lawrence Junior High</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1931">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1932">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1933">
                <li data-id="element-1934">• Spring Valley High School</li>
                <li data-id="element-1935">• Bonanza High School</li>
                <li data-id="element-1936">• Clark High School (Nearby)</li>
                <li data-id="element-1937">• Durango High School (Nearby)</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-1938">
                  Clark High School is renowned for its prestigious AMSAT and
                  Finance magnet programs.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-warmGray p-8 border border-warmGray-dark" data-id="element-1939">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1940">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1941">
                <li data-id="element-1942">• Calvary Chapel Christian School</li>
                <li data-id="element-1943">• Spring Valley Montessori</li>
                <li data-id="element-1944">• Doral Academy (Multiple Campuses)</li>
                <li data-id="element-1945">• Discovery Charter School</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1946">
        <div className="max-w-7xl mx-auto" data-id="element-1947">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1948">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1949" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1950">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1951">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1952">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1953">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1954">
                <img src="https://cdn.magicpatterns.com/uploads/rV3C9fFs42XxwuKoosfryr/Desert_Breeze_Park.jpg" alt="Desert Breeze Park aerial view with lighted sports fields in Spring Valley Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1955" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1956">
                Desert Breeze Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1957">
                The centerpiece of Spring Valley recreation. This massive
                regional park features an indoor/outdoor aquatic facility, skate
                park, dog runs, and numerous baseball and soccer fields.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1958">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1959">
                <img src="https://cdn.magicpatterns.com/uploads/usbLkfz7NMQbvdDVdyDwLj/Charlie_Frias_Park.jpg" alt="Charlie Frias Park playground with shade structures and splash pad in Spring Valley Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1960" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1961">
                Charlie Frias Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1962">
                A beautiful 32-acre park offering spectacular views of the Strip
                and mountains, featuring a dog park, splash pad, playgrounds,
                and multi-use sports fields.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1963">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1964">
                <img src="https://cdn.magicpatterns.com/uploads/nsnWsBwFHRGBJdSUd4SNhG/Paul_Meyer_Park.webp" alt="Paul Meyer Park entrance sign Clark County Parks and Recreation in Spring Valley Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1965" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1966">
                Paul Meyer Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1967">
                A beloved neighborhood park with mature shade trees, tennis
                courts, basketball courts, and a community center offering
                classes and activities for all ages.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-1968">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1969">
                <img src="https://cdn.magicpatterns.com/uploads/6RXARuCekHUz6CWmCW87fx/spanish-trail.jpg" alt="Spanish Trail Country Club aerial view of championship golf course with water features and luxury homes in Las Vegas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1970" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1971">
                Spanish Trail Country Club
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1972">
                An exclusive 27-hole championship golf course designed by Robert
                Trent Jones Jr., offering lush fairways, water features, and an
                elegant clubhouse.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-1973">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1974">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1975">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1976" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1977">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1978">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1979">
              Spring Valley residents are served by the exceptional{' '}
              <strong data-id="element-1980">Spring Valley Hospital Medical Center</strong>, a
              comprehensive facility known for its advanced maternity center,
              open-heart surgery program, and dedicated emergency care.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1981">
              The community's central location also provides immediate access to
              the specialized medical corridors along Flamingo Road and
              Tropicana Avenue, featuring dozens of specialty clinics and
              wellness centers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-1982">
        <div className="max-w-7xl mx-auto" data-id="element-1983">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1984">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1985" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1986">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-1987">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1988">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1989">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1990" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1991">215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1992">
                Forms the western border of Spring Valley, providing rapid
                transit to Summerlin and Henderson.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1993">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1994" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1995">I-15 Access</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1996">
                Easily accessible via major east-west corridors like Flamingo,
                Tropicana, and Russell Road.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1997">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1998" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1999">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2000">
                A highly convenient 10-15 minute drive, making it one of the
                best locations for frequent travelers.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-2001">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-2002" />
              <h4 className="text-xl font-serif mb-2" data-id="element-2003">The Strip</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-2004">
                Spring Valley borders the Strip to the west, offering
                unparalleled access to world-class entertainment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Spring Valley" data-id="element-2005" />

      <ListingsComingSoon area="Spring Valley" data-id="element-2006" />

      <ContactCTA data-id="element-2007" />
    </main>;
}